import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useTenantContext } from '../auth/useTenantContext';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

export const AssignmentPickListSummarySchema = z.object({
  id: z.number(),
  external_id: z.string(),
  qty_total: z.number(),
  item_count: z.number(),
  distinct_locations: z.number(),
  auto_split_pending: z.boolean(),
  created_at: z.string().nullable().optional(),
  // Cliente "principale" (prima riga per seq) + n. clienti distinti della lista.
  // Optional per back-compat durante il deploy progressivo del backend.
  customer_code: z.string().nullable().optional(),
  customer_name: z.string().nullable().optional(),
  customer_count: z.number().optional(),
});
export type AssignmentPickListSummary = z.infer<typeof AssignmentPickListSummarySchema>;

const PoolWholeListCard = z.object({
  kind: z.literal('whole_list'),
  pick_list: AssignmentPickListSummarySchema,
});
const PoolShardCard = z.object({
  kind: z.literal('self_claim_shard'),
  mission_id: z.number(),
  shard_index: z.number(),
  shard_total: z.number(),
  qty_assigned_total: z.number(),
  task_count: z.number(),
  pick_list: AssignmentPickListSummarySchema,
});
const PoolCardSchema = z.discriminatedUnion('kind', [PoolWholeListCard, PoolShardCard]);
export type PoolCard = z.infer<typeof PoolCardSchema>;

export const MissionStatusSchema = z.enum(['pending', 'assigned', 'active', 'awaiting_close', 'done']);
export type MissionStatus = z.infer<typeof MissionStatusSchema>;

const AssignedWholeListMission = z.object({
  kind: z.literal('whole_list'),
  mission_id: z.number(),
  status: MissionStatusSchema,
  claim_order: z.number().nullable(),
  shard_index: z.number(),
  shard_total: z.number(),
  qty_assigned_total: z.number(),
  task_count: z.number(),
  pick_list: AssignmentPickListSummarySchema,
});
const AssignedShardMission = z.object({
  kind: z.literal('self_claim_shard'),
  mission_id: z.number(),
  status: MissionStatusSchema,
  claim_order: z.number().nullable(),
  shard_index: z.number(),
  shard_total: z.number(),
  qty_assigned_total: z.number(),
  task_count: z.number(),
  pick_list: AssignmentPickListSummarySchema,
});
const AssignedWizardShardMission = z.object({
  kind: z.literal('wizard_shard'),
  mission_id: z.number(),
  status: MissionStatusSchema,
  claim_order: z.number().nullable(),
  shard_index: z.number(),
  shard_total: z.number(),
  qty_assigned_total: z.number(),
  task_count: z.number(),
  pick_list: AssignmentPickListSummarySchema,
});
const AssignedMissionSchema = z.discriminatedUnion('kind', [
  AssignedWholeListMission, AssignedShardMission, AssignedWizardShardMission,
]);
export type AssignedMission = z.infer<typeof AssignedMissionSchema>;

export const AssignmentBoardSchema = z.object({
  warehouse_id: z.number(),
  operators: z.array(z.object({
    user_id: z.number(),
    email: z.string().nullable(),
    label: z.string(),
  })),
  pool: z.array(PoolCardSchema),
  assignments: z.array(z.object({
    operator_user_id: z.number(),
    missions: z.array(AssignedMissionSchema),
  })),
});
export type AssignmentBoard = z.infer<typeof AssignmentBoardSchema>;

async function authedFetch(
  path: string,
  init: RequestInit,
  token: string,
  tenantCode: string,
  warehouseId: number | null = null,
): Promise<unknown> {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> ?? {}),
    Authorization: `Bearer ${token}`,
    'X-Tenant-Id': tenantCode,
  };
  if (warehouseId !== null) headers['X-Warehouse-Id'] = String(warehouseId);
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text();
    throw Object.assign(new Error(`${res.status}: ${body}`), { status: res.status, body });
  }
  if (res.status === 204) return null;
  return res.json();
}

const assignmentBoardKey = (warehouseId: number) => ['admin', 'assignments', 'board', warehouseId];

export function useAssignmentBoard(token: string | null, tenantCode: string | null, warehouseId: number | null) {
  return useQuery<AssignmentBoard>({
    queryKey: assignmentBoardKey(warehouseId ?? -1),
    enabled: token !== null && tenantCode !== null && warehouseId !== null,
    // SP-8c — board operativa: refetch periodico (8s) + al focus, meno critico
    // della dashboard ma comunque "vivo".
    refetchInterval: 8_000,
    refetchOnWindowFocus: true,
    queryFn: async () => AssignmentBoardSchema.parse(
      await authedFetch(`/admin/assignments?warehouse_id=${warehouseId}`, {}, token!, tenantCode!, warehouseId),
    ),
  });
}

function mutationHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'Idempotency-Key': crypto.randomUUID(),
  };
}

interface MutationCtx { warehouseId: number }

export function useAssignWhole(token: string | null, tenantCode: string | null, ctx: MutationCtx) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { pickListId: number; operatorUserId: number }) =>
      authedFetch('/admin/assignments/assign-whole', {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({ pick_list_id: vars.pickListId, operator_user_id: vars.operatorUserId }),
      }, token!, tenantCode!, warehouseId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: assignmentBoardKey(ctx.warehouseId) });
      // The pick-list list/detail status reflects mission state — invalidate both.
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists'] });
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists', 'counts'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit', 'tenant'] });
    },
  });
}

export function useAssignOperator(token: string | null, tenantCode: string | null, ctx: MutationCtx) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { missionId: number; operatorUserId: number }) =>
      authedFetch('/admin/assignments/assign-operator', {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({ mission_id: vars.missionId, operator_user_id: vars.operatorUserId }),
      }, token!, tenantCode!, warehouseId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: assignmentBoardKey(ctx.warehouseId) });
      // The pick-list list/detail status reflects mission state — invalidate both.
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists'] });
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists', 'counts'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit', 'tenant'] });
    },
  });
}

export function useCancelWhole(token: string | null, tenantCode: string | null, ctx: MutationCtx) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { missionId: number }) =>
      authedFetch('/admin/assignments/cancel-whole', {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({ mission_id: vars.missionId }),
      }, token!, tenantCode!, warehouseId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: assignmentBoardKey(ctx.warehouseId) });
      // The pick-list list/detail status reflects mission state — invalidate both.
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists'] });
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists', 'counts'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit', 'tenant'] });
    },
  });
}

export function useUnassignOperator(token: string | null, tenantCode: string | null, ctx: MutationCtx) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { missionId: number }) =>
      authedFetch('/admin/assignments/unassign-operator', {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({ mission_id: vars.missionId }),
      }, token!, tenantCode!, warehouseId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: assignmentBoardKey(ctx.warehouseId) });
      // The pick-list list/detail status reflects mission state — invalidate both.
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists'] });
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists', 'counts'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit', 'tenant'] });
    },
  });
}

export function useReorder(token: string | null, tenantCode: string | null, ctx: MutationCtx) {
  const qc = useQueryClient();
  const { warehouseId } = useTenantContext();
  return useMutation({
    mutationFn: async (vars: { operatorUserId: number; missionIds: number[] }) =>
      authedFetch('/admin/assignments/reorder', {
        method: 'POST',
        headers: mutationHeaders(),
        body: JSON.stringify({ operator_user_id: vars.operatorUserId, mission_ids: vars.missionIds }),
      }, token!, tenantCode!, warehouseId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: assignmentBoardKey(ctx.warehouseId) });
      // The pick-list list/detail status reflects mission state — invalidate both.
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists'] });
      qc.invalidateQueries({ queryKey: ['admin', 'pick-lists', 'counts'] });
      qc.invalidateQueries({ queryKey: ['admin', 'audit', 'tenant'] });
    },
  });
}
